import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { LuArrowRight, LuBox, LuChartLine, LuCircleDot, LuCrosshair, LuFileSearch, LuFlag, LuHouse, LuKeyboard, LuLayers, LuNotebookPen, LuPlay, LuSearch, LuSettings, LuShapes, LuSparkles, LuTarget } from "react-icons/lu";
import { CoachReview } from "./CoachReview";
import { coachPatterns, coachSessions, frameSrc, frameSrcSet, trendLabel, type SessionId } from "./coach";

type View = "home" | "review" | "progress" | "record";

const views: { id: View; label: string; icon: ReactNode; crumb: string; blurb: string }[] = [
  { id: "review", label: "Coach review", icon: <LuFileSearch />, crumb: "Review › Coach review", blurb: "The moments that cost you, what a pro would do instead, and one drill for next time." },
  { id: "progress", label: "Patterns", icon: <LuChartLine />, crumb: "Progress › Patterns", blurb: "What keeps showing up across sessions, and what’s finally gone." },
  { id: "home", label: "Home", icon: <LuHouse />, crumb: "Dashboard", blurb: "Your latest session, your practice days, and the goals you’re working on." },
  { id: "record", label: "Record", icon: <LuCircleDot />, crumb: "Record", blurb: "One shortcut to record, one to mark a moment. Everything stays on your machine." },
];
const order: View[] = views.map(v => v.id);
const sessionIcon: Record<SessionId, ReactNode> = { cs2: <LuCrosshair />, blender: <LuBox /> };

function Sidebar({ view, onPick }: { view: View; onPick: (v: View) => void }) {
  const item = (id: View | null, label: string, icon: ReactNode) => <button type="button" tabIndex={-1} className={view === id ? "on" : ""} onClick={() => id && onPick(id)}>{icon}<span>{label}</span></button>;
  return <nav className="ap-side" aria-hidden="true">
    {item("home", "Home", <LuHouse />)}
    {item("record", "Record", <LuCircleDot />)}
    <small>Review</small>
    {item(null, "Sessions", <LuLayers />)}
    {item("review", "Review", <LuFileSearch />)}
    {item("progress", "Progress", <LuChartLine />)}
    <small>Practice</small>
    {item(null, "Activities", <LuShapes />)}
    {item(null, "Sample reviews", <LuSparkles />)}
    <div className="ap-side-foot">
      {item(null, "Search", <LuSearch />)}
      {item(null, "Settings", <LuSettings />)}
      <div className="ap-capture"><i /><span><strong>Capture off</strong><small>Local only</small></span></div>
    </div>
  </nav>;
}

const days = Array.from({ length: 28 }, (_, i) => { const d = new Date(Date.UTC(2026, 8, 8 + i)); return d.getUTCDate(); });
const practiced = new Set([9, 11, 14, 16, 19, 21, 23, 24, 25, 26]);

function Home() {
  const s = coachSessions.cs2;
  return <div className="ap-view ap-home">
    <div className="ap-head"><div><h4>Welcome back</h4><span>Alex · 1 completed / 3 goals</span></div><button type="button" tabIndex={-1} className="ap-btn">Import recording</button><button type="button" tabIndex={-1} className="ap-btn hot"><LuCircleDot /> Start recording</button></div>
    <div className="ap-home-top">
      <div className="ap-card ap-latest">
        <div className="ap-thumb"><img src={frameSrc("cs2", 720)} srcSet={frameSrcSet("cs2")} sizes="460px" width={1280} height={720} alt="" loading="lazy" decoding="async" /><span className="ap-pill">Latest session</span><span className="ap-dur">00:{s.length}</span></div>
        <div className="ap-latest-row"><span className="ap-play"><LuPlay /></span><div><strong>{s.title}</strong><small>Oct 4 · 7:00 PM</small></div><em>Open review <LuArrowRight /></em></div>
      </div>
      <div className="ap-card ap-activity">
        <div className="ap-card-title"><span>Activity</span><em>Last 28 days</em></div>
        <div className="ap-cal">{days.map((d, i) => <i key={i} className={practiced.has(i) ? "on" : ""} style={{ "--i": i } as CSSProperties}>{String(d).padStart(2, "0")}</i>)}</div>
        <div className="ap-stats"><div><b>10</b><small>Sessions</small></div><div><b>5h 31m</b><small>Recorded</small></div><div><b>3</b><small>Notes</small></div></div>
      </div>
    </div>
    <div className="ap-home-bottom">
      <div className="ap-card"><div className="ap-card-title"><span><LuTarget /> Active goals</span><em>All goals</em></div>
        {[["Counter-strafe before every first shot", "Games"], ["Mirror modifier before the first edit", "3D & CAD"]].map(([g, k]) => <div key={g} className="ap-goal"><i /><div><strong>{g}</strong><small>{k}</small></div><em>Active</em></div>)}
      </div>
      <div className="ap-card"><div className="ap-card-title"><span><LuNotebookPen /> Saved notes</span><em>Review inbox</em></div>
        {[["00:07:38", "Lost the ramp duel while still strafing.", "Mirage Premier match"], ["00:04:20", "Built the right cuff by hand after finishing the left.", "Glassmaker character pass"]].map(([t, n, from]) => <div key={t} className="ap-note"><code>{t}</code><div><strong>{n}</strong><small>{from}</small></div></div>)}
      </div>
    </div>
  </div>;
}

function Review() {
  const [id, setId] = useState<SessionId>("cs2");
  return <div className="ap-view ap-review">
    <div className="ap-subtabs"><span className="on">Coach review</span><span>Review inbox</span><span>Session notes</span><span>Compare</span>
      <div className="ap-sessions">{(Object.keys(coachSessions) as SessionId[]).map(key => <button type="button" tabIndex={-1} key={key} className={id === key ? "on" : ""} onClick={() => setId(key)}>{sessionIcon[key]}{coachSessions[key].title}</button>)}</div>
    </div>
    <CoachReview key={id} initial={id} locked variant="app" />
  </div>;
}

function Progress() {
  return <div className="ap-view ap-progress">
    <div className="ap-head"><div><h4>What Hindset remembers</h4><span>Patterns from your last 6 sessions, across everything you practice.</span></div></div>
    <div className="ap-insight"><div><small>Across your activities</small><strong>You start loose in everything you practice.</strong><p>A third of this week’s moments came in your first ten minutes, in Counter-Strike 2 and in Blender. A two-minute warm-up is the cheapest fix Hindset can suggest.</p></div><span className="ap-insight-icons" aria-hidden="true"><LuCrosshair /><b>+</b><LuBox /></span></div>
    <div className="ap-patterns">{coachPatterns.map((p, pi) => <div key={p.title} className="ap-card ap-pattern">
      <div className="ap-pattern-top"><span>{sessionIcon[p.session]}{coachSessions[p.session].app}</span><em className={p.trend}>{trendLabel[p.trend]}</em></div>
      <strong>{p.title}</strong><p>{p.detail}</p>
      <div className="ap-dots">{p.history.map((hit, i) => <i key={i} className={hit ? "hit" : ""} style={{ "--d": `${pi * 60 + i * 70}ms` } as CSSProperties} />)}<small>{p.history.filter(Boolean).length} of last {p.history.length} sessions</small></div>
      <div className="ap-drill"><LuTarget /> Drill: <b>{p.drill}</b></div>
    </div>)}</div>
  </div>;
}

function Record() {
  return <div className="ap-view ap-record">
    <div className="ap-head"><div><h4>Record</h4><span>Focused monitor · 2560×1440 · 60 FPS</span></div></div>
    <div className="ap-record-grid">
      <div className="ap-card ap-rec-card">
        <div className="ap-rec-big"><span className="ap-rec-dot" /><div><b>00:14:22</b><small>Recording · 3 moments marked</small></div></div>
        <div className="ap-card-title ap-wave-title"><span>Key activity</span><em>Timing only · never text</em></div>
        <div className="ap-wave">{Array.from({ length: 56 }, (_, i) => <i key={i} style={{ animationDelay: `${(i % 14) * 70}ms`, height: `${22 + ((i * 37) % 78)}%` }} />)}</div>
        <div className="ap-rec-actions"><button type="button" tabIndex={-1} className="ap-btn hot">Stop recording</button><button type="button" tabIndex={-1} className="ap-btn"><LuFlag /> Mark moment</button></div>
      </div>
      <div className="ap-card">
        <div className="ap-card-title"><span><LuKeyboard /> Shortcuts</span></div>
        {[["Start / stop", ["Ctrl", "Shift", "R"]], ["Mark moment", ["Ctrl", "Shift", "M"]]].map(([l, keys]) => <div key={String(l)} className="ap-row"><span>{l}</span><span>{(keys as string[]).map(k => <kbd key={k}>{k}</kbd>)}</span></div>)}
        <div className="ap-card-title ap-gap"><span>Sources</span></div>
        {[["Screen", "On"], ["Key timing", "On"], ["Recording indicator", "Hidden in fullscreen"], ["Storage", "This device"]].map(([a, b]) => <div key={a} className="ap-row"><span>{a}</span><span className="ap-ok">{b}</span></div>)}
      </div>
    </div>
  </div>;
}

export function AppShowcase() {
  const [view, setView] = useState<View>("review");
  const [auto, setAuto] = useState(true);
  const frame = useRef<HTMLDivElement>(null);
  const [tilt, setTilt] = useState(1);

  useEffect(() => {
    const el = frame.current;
    if (!el || window.matchMedia("(prefers-reduced-motion: reduce)").matches) { setTilt(0); return; }
    let raf = 0;
    const update = () => { raf = 0; const r = el.getBoundingClientRect(); const p = Math.min(1, Math.max(0, (r.top - window.innerHeight * .12) / (window.innerHeight * .7))); setTilt(p); };
    const on = () => { if (!raf) raf = requestAnimationFrame(update); };
    update(); window.addEventListener("scroll", on, { passive: true }); window.addEventListener("resize", on);
    return () => { window.removeEventListener("scroll", on); window.removeEventListener("resize", on); cancelAnimationFrame(raf); };
  }, []);

  useEffect(() => {
    if (!auto) return;
    const t = window.setInterval(() => setView(v => order[(order.indexOf(v) + 1) % order.length]!), 7000);
    return () => window.clearInterval(t);
  }, [auto]);

  const pick = (v: View) => { setAuto(false); setView(v); };
  const current = views.find(v => v.id === view)!;

  return <div className="ax">
    <div className="ax-picker" role="tablist" aria-label="App views">{views.map(v => <button key={v.id} role="tab" type="button" aria-label={v.label} aria-selected={view === v.id} onClick={() => pick(v.id)}>{v.icon}<span>{v.label}</span>{view === v.id && auto && <i className="ax-timer" />}</button>)}</div>
    <p className="ax-blurb" aria-live="polite">{current.blurb}</p>
    <div className="ax-stage" ref={frame} style={{ "--t": tilt } as CSSProperties}>
      <div className="ax-glow" aria-hidden="true" />
      <div className="ax-window ap-window" role="img" aria-label={`The Hindset desktop app, ${current.label} view, with sample data`}>
        <div className="ap-bar"><span className="ax-dots"><i /><i /><i /></span><span className="ap-brand"><img src="/brand/hindset-mark.svg" alt="" width={16} height={16} />hindset</span><span className="ap-crumb">{current.crumb}</span><span className="ap-search"><LuSearch />Search Hindset<kbd>Ctrl K</kbd></span><span className="ap-record"><i />Start recording</span></div>
        <div className="ap-body"><Sidebar view={view} onPick={pick} /><div className="ap-main" key={view}>{view === "home" ? <Home /> : view === "review" ? <Review /> : view === "progress" ? <Progress /> : <Record />}</div></div>
      </div>
    </div>
    <p className="hs-footnote">The Hindset desktop app with sample data.</p>
  </div>;
}
